// PMax Budget Constraints — Budgets section, deterministic check.
// Per-campaign daily budget + whether it's limited by budget (Google budget recommendation),
// budget-limited campaigns first.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';
import { usd } from '../../utils/format';

interface CampaignRow {
  id?: string;
  name: string;
  dailyBudget: number | null;
  limited: boolean;
  recommendedBudget: number | null;
}

interface BudgetShape {
  totalCampaigns: number;
  limitedCount: number;
  campaigns: CampaignRow[];
}

function money(n: number | null): string {
  return usd(n, { perDay: true });
}

export const PmaxBudgetConstraintsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as BudgetShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Budget constraints
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalCampaigns} PMax campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalCampaigns.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>PMax campaigns</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.limitedCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Limited by budget</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Per-campaign budget</h4>
        <table className='evidence__table evidence__table--pmax-budget'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th className='evidence__th--right'>Daily budget</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => (
              <tr
                key={c.id ?? c.name}
                className={classNames({ 'evidence__row--flagged': c.limited })}
              >
                <td>{c.name}</td>
                <td className='evidence__td--right'>{money(c.dailyBudget)}</td>
                <td>
                  {c.limited ? (
                    <span className='evidence__strength evidence__strength--average'>
                      Limited
                      {c.recommendedBudget != null ? ` · rec. ${money(c.recommendedBudget)}` : ''}
                    </span>
                  ) : (
                    <span className='evidence__strength evidence__strength--good'>Not limited</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        "Limited by budget" is detected via Google's budget recommendation — when the cap is the
        bottleneck, Google surfaces a higher recommended amount. Tinuiti recommends keeping budgets
        unconstrained and managing scale through target (ROAS/CPA) adjustments rather than caps, so
        budget-limited campaigns are worth reviewing for a raise (unless the cap is an intentional
        pacing choice).
      </p>
    </div>
  );
};
